"use client";

import { useState } from "react";
import { useUiStore } from "@/store/ui";
import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { LoadingState, EmptyState, PageHeader } from "@/components/features/states";
import {
  Store as StoreIcon,
  Star,
  MapPin,
  Search,
  Utensils,
  ShoppingCart,
  Pill,
  ShoppingBag,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Store } from "@/lib/types";

const CATEGORIES = [
  { key: "all", icon: ShoppingBag },
  { key: "restaurant", icon: Utensils },
  { key: "grocery", icon: ShoppingCart },
  { key: "pharmacy", icon: Pill },
  { key: "convenience", icon: StoreIcon },
] as const;

export function StoresListView() {
  const navigate = useUiStore((s) => s.navigate);
  const { t } = useTranslation();
  const route = useUiStore((s) => s.route);
  // Read category from hash query (e.g. app/stores?category=restaurant)
  const initialCategory =
    route.params && (route as any).path?.includes("?")
      ? new URLSearchParams((route as any).path.split("?")[1]).get("category") || "all"
      : "all";
  const [category, setCategory] = useState(initialCategory);
  const [query, setQuery] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["stores", "list", category, query],
    queryFn: () =>
      api.stores.list({
        category: category === "all" ? undefined : category,
        q: query || undefined,
      }),
    staleTime: 30 * 1000,
  });
  const stores: Store[] = data?.data?.stores ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("stores.title")}
        subtitle={t("stores.subtitle")}
      />

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("stores.search")}
          className="pl-9"
        />
      </div>

      {/* Category filter chips */}
      <div className="scroll-area -mx-3 flex gap-2 overflow-x-auto px-3 pb-1">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.key}
            onClick={() => setCategory(cat.key)}
            className={`flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition active:scale-95 ${
              category === cat.key
                ? "bg-primary text-primary-foreground shadow"
                : "bg-muted text-muted-foreground hover:bg-muted/70"
            }`}
          >
            <cat.icon className="h-3.5 w-3.5" />
            {cat.key === "all" ? t("stores.all") : t(`stores.${cat.key}`)}
          </button>
        ))}
      </div>

      {/* Stores list */}
      {isLoading ? (
        <LoadingState />
      ) : stores.length === 0 ? (
        <EmptyState
          icon={<StoreIcon className="h-8 w-8" />}
          title={t("stores.noResults")}
          description={t("stores.noResultsDesc")}
        />
      ) : (
        <div className="space-y-3">
          {stores.map((store) => (
            <StoreListCard key={store.id} store={store} />
          ))}
        </div>
      )}
    </div>
  );
}

function StoreListCard({ store }: { store: Store }) {
  const navigate = useUiStore((s) => s.navigate);
  const { t } = useTranslation();
  const isSponsored = store._sponsored === true;
  return (
    <Card
      className={`cursor-pointer overflow-hidden transition hover:shadow-md active:scale-[0.99] ${
        isSponsored
          ? "border-amber-300 bg-gradient-to-br from-amber-50/60 via-card to-card dark:border-amber-700/50 dark:from-amber-950/20"
          : ""
      }`}
      onClick={() => navigate(`app/stores/${store.id}`)}
    >
      <div className="flex gap-3 p-3 sm:p-4">
        <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-muted sm:h-24 sm:w-24">
          {store.imageUrl ? (
            <img
              src={store.imageUrl}
              alt={store.name}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-muted-foreground">
              <StoreIcon className="h-8 w-8" />
            </div>
          )}
          {isSponsored && (
            <div className="absolute left-0 top-0 flex items-center gap-0.5 rounded-br-lg bg-amber-500 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white shadow">
              <Sparkles className="h-2.5 w-2.5" />
              Sponsored
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="line-clamp-1 font-bold">{store.name}</p>
                {isSponsored && (
                  <Badge className="shrink-0 bg-amber-500 text-[10px] text-white hover:bg-amber-500">
                    <Sparkles className="mr-0.5 h-2.5 w-2.5" /> Sponsored
                  </Badge>
                )}
              </div>
              {store.description && (
                <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
                  {store.description}
                </p>
              )}
            </div>
            {store.featured && !isSponsored && (
              <Badge className="shrink-0 bg-amber-500 text-[10px] text-white hover:bg-amber-500">
                <Star className="mr-0.5 h-2.5 w-2.5" /> {t("stores.featured")}
              </Badge>
            )}
          </div>
          <div className="mt-auto flex items-center gap-3 pt-2 text-xs">
            <span className="flex items-center gap-0.5 font-semibold text-amber-600">
              <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
              {store.rating.toFixed(1)}
              <span className="font-normal text-muted-foreground">
                ({store.ratingCount})
              </span>
            </span>
            {store._distanceKm != null && (
              <span className="flex items-center gap-0.5 text-muted-foreground">
                <MapPin className="h-3 w-3" /> {store._distanceKm} {t("quote.km")} {t("stores.away")}
              </span>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
